import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { IoRefresh, IoArrowForward, IoWarning, IoCheckmarkCircle, IoGitCompare, IoDownload } from 'react-icons/io5';

interface EnvState {
  status: string;
  appliedAt: string | null;
  appliedBy: string | null;
  durationMs: number | null;
  checksum: string | null;
  checksumMatch: boolean | null;
  isReversible: boolean | null;
  isDestructive: boolean | null;
  error: string | null;
}

interface CompareItem {
  name: string;
  filename: string | null;
  fileChecksum: string | null;
  dev: EnvState;
  prod: EnvState;
  syncStatus: 'perfect' | 'dev_ahead' | 'prod_ahead' | 'divergent';
  flags: {
    checksumDrift: boolean;
    ghost: 'dev' | 'prod' | 'both' | null;
  };
}

interface CompareSummary {
  total: number;
  match: number;
  devAhead: number;
  prodAhead: number;
  divergent: number;
  drift: number;
  ghosts: number;
  syncHealthPct: number;
  dev: {
    applied: number;
    pending: number;
    failed: number;
  };
  prod: {
    applied: number;
    pending: number;
    failed: number;
  };
}

interface CompareData {
  success: boolean;
  summary: CompareSummary;
  items: CompareItem[];
}

const CompareMatrix: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'gaps' | 'divergent' | 'drift' | 'ghosts'>('all');

  const { data, isLoading, refetch } = useQuery<CompareData>(
    'migrations-compare',
    async () => {
      const response = await axios.get('/api/v1/admin/migrations/compare');
      return response.data;
    },
    {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  );

  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    const term = searchQuery.trim().toLowerCase();
    
    return items.filter(item => {
      // Search filter
      if (term && !item.name.toLowerCase().includes(term)) {
        return false;
      }

      // Status filter
      if (filter === 'gaps') {
        return item.syncStatus === 'dev_ahead' || item.syncStatus === 'prod_ahead';
      }
      if (filter === 'divergent') {
        return item.syncStatus === 'divergent';
      }
      if (filter === 'drift') {
        return item.flags.checksumDrift;
      }
      if (filter === 'ghosts') {
        return !!item.flags.ghost;
      }

      return true;
    });
  }, [data, searchQuery, filter]);

  if (isLoading) {
    return <div className="p-4">Loading comparison...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <IoGitCompare size={28} />
            Dev vs Prod Comparison
          </h2>
          <p className="text-sm text-gray-600">Side-by-side migration status across environments</p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
          disabled={isLoading}
        >
          <IoRefresh size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <SummaryCard
          label="Sync Health"
          value={`${data?.summary.syncHealthPct || 100}%`}
          subtitle={`${data?.summary.match || 0}/${data?.summary.total || 0} in sync`}
          color="blue"
        />
        <SummaryCard
          label="Dev Ahead"
          value={data?.summary.devAhead || 0}
          subtitle="Ready to promote"
          color="amber"
        />
        <SummaryCard
          label="Prod Ahead"
          value={data?.summary.prodAhead || 0}
          subtitle="Missing in dev"
          color="orange"
        />
        <SummaryCard
          label="Divergent"
          value={data?.summary.divergent || 0}
          subtitle="Needs attention"
          color="red"
        />
        <SummaryCard
          label="Drift / Ghosts"
          value={`${data?.summary.drift || 0} / ${data?.summary.ghosts || 0}`}
          subtitle="File changes / Missing files"
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-3 items-center flex-wrap justify-between">
          <div className="flex gap-3 items-center flex-wrap">
            <input
              type="text"
              placeholder="Search migrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="min-w-[200px] border rounded px-3 py-2"
            />
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-2 rounded text-sm ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('gaps')}
              className={`px-3 py-2 rounded text-sm ${
                filter === 'gaps' ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Gaps
            </button>
            <button
              onClick={() => setFilter('divergent')}
              className={`px-3 py-2 rounded text-sm ${
                filter === 'divergent' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Divergent
            </button>
            <button
              onClick={() => setFilter('drift')}
              className={`px-3 py-2 rounded text-sm ${
                filter === 'drift' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Drift
            </button>
            <button
              onClick={() => setFilter('ghosts')}
              className={`px-3 py-2 rounded text-sm ${
                filter === 'ghosts' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Ghosts
            </button>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Migration
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dev Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prod Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sync
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr key={item.name} className={item.flags.ghost ? 'bg-purple-50' : ''}>
                  <td className="px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{item.filename || 'MISSING'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge state={item.dev} env="DEV" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge state={item.prod} env="PROD" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <SyncBadge item={item} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <RowActions item={item} refetch={refetch} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No migrations match the current filter
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}> = ({ label, value, subtitle, color }) => {
  const colorMap: Record<string, string> = {
    blue: 'border-blue-500',
    amber: 'border-amber-500',
    orange: 'border-orange-500',
    red: 'border-red-500',
    purple: 'border-purple-500',
    green: 'border-green-500'
  };

  return (
    <div className={`p-3 bg-white rounded shadow border-l-4 ${colorMap[color]}`}>
      <div className="text-xs text-gray-600 uppercase">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
};

const StatusBadge: React.FC<{ state: EnvState; env: 'DEV' | 'PROD' }> = ({ state, env }) => {
  const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
    applied: { bg: 'bg-green-100', text: 'text-green-700', label: 'Applied' },
    failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
    rolled_back: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Rolled Back' },
    running: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Running' }
  };

  const style = statusStyles[state.status] || statusStyles.pending;
  const drift = state.checksumMatch === false;

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`px-2 py-1 ${style.bg} ${style.text} text-xs rounded font-medium`}>
        {style.label}
      </span>
      {drift && (
        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-300 text-xs rounded font-medium">
          Drift
        </span>
      )}
      {state.appliedAt && (
        <div className="text-xs text-gray-500">{new Date(state.appliedAt).toLocaleDateString()}</div>
      )}
    </div>
  );
};

const SyncBadge: React.FC<{ item: CompareItem }> = ({ item }) => {
  const syncStyles: Record<string, { bg: string; text: string; label: string; icon: string }> = {
    perfect: { bg: 'bg-green-100', text: 'text-green-700', label: 'Match', icon: '✅' },
    dev_ahead: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Dev Ahead', icon: '⚠️' },
    prod_ahead: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Prod Ahead', icon: '🔄' },
    divergent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Divergent', icon: '❌' }
  };

  const style = syncStyles[item.syncStatus] || syncStyles.divergent;
  const ghostText = item.flags.ghost ? ` · Ghost: ${item.flags.ghost}` : '';

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`px-2 py-1 ${style.bg} ${style.text} text-xs rounded font-medium border ${style.bg.replace('bg-', 'border-')}`}>
        {style.icon} {style.label}{ghostText}
      </span>
    </div>
  );
};

const RowActions: React.FC<{ item: CompareItem; refetch: () => void }> = ({ item, refetch }) => {
  const handlePromoteToProd = async () => {
    if (!window.confirm(`Promote "${item.name}" to Production?\n\nThis will apply the migration to the production database.`)) {
      return;
    }

    try {
      await axios.post('/api/v1/admin/migrations/bulk/apply', {
        target: 'prod',
        names: [item.name]
      });
      alert('Migration promoted to Production successfully!');
      refetch();
    } catch (error: any) {
      console.error('Error promoting migration:', error);
      if (error.response?.data?.requiresApproval) {
        alert(`Approval required: ${error.response.data.message}`);
      } else {
        alert(`Failed to promote migration: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleApplyToDev = async () => {
    if (!window.confirm(`Apply "${item.name}" to Development?\n\nThis will apply the migration to the development database.`)) {
      return;
    }

    try {
      await axios.post('/api/v1/admin/migrations/bulk/apply', {
        target: 'dev',
        names: [item.name]
      });
      alert('Migration applied to Development successfully!');
      refetch();
    } catch (error: any) {
      console.error('Error applying migration:', error);
      alert(`Failed to apply migration: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleViewDiff = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/migrations/${item.name}/diff`);
      // Dispatch custom event to open existing diff modal
      window.dispatchEvent(new CustomEvent('migration:openDiff', { 
        detail: { name: item.name, data: response.data } 
      }));
    } catch (error) {
      console.error('Error fetching diff:', error);
      alert('Failed to load diff');
    }
  };

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={handleViewDiff}
        className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
      >
        Diff
      </button>
      
      {item.syncStatus === 'dev_ahead' && (
        <button
          onClick={handlePromoteToProd}
          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
        >
          <IoArrowForward size={12} />
          Promote → Prod
        </button>
      )}
      
      {item.syncStatus === 'prod_ahead' && (
        <button
          onClick={handleApplyToDev}
          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
        >
          Apply to Dev
        </button>
      )}
      
      {item.flags.checksumDrift && (
        <span className="px-2 py-1 bg-orange-100 text-orange-700 border border-orange-300 text-xs rounded font-medium flex items-center gap-1">
          <IoWarning size={12} />
          Drift
        </span>
      )}
    </div>
  );
};

export default CompareMatrix;

